import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Bar } from 'react-chartjs-2';
import { Funnel } from '@phosphor-icons/react';
import { AcquisitionPhaseBucket } from '../types/statistics.types';
import { COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionPhaseBucket[];
  loading: boolean;
}

// Order phases in pipeline order, with terminal phases last
const PHASE_ORDER = [
  'Inbox',
  'Opgepikt',
  'Agenda ACQ meeting',
  'Agenda RVB',
  'Deal!',
  'Afgewezen',
];

const PHASE_COLOR: Record<string, string> = {
  Inbox: '#9CA3AF',
  Opgepikt: '#3B82F6',
  'Agenda ACQ meeting': '#8B5CF6',
  'Agenda RVB': '#F59E0B',
  'Deal!': '#22C55E',
  Afgewezen: '#F43F5E',
};

export const PhaseFunnelChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const map = new Map(data.map((d) => [d.phase, d.count]));
    const ordered = PHASE_ORDER.filter((p) => map.has(p)).map((p) => ({
      phase: p,
      count: map.get(p) || 0,
    }));
    // Append any unknown phases (shouldn't happen but safe)
    data.forEach((d) => {
      if (!PHASE_ORDER.includes(d.phase)) {
        ordered.push({ phase: d.phase, count: d.count });
      }
    });

    return {
      labels: ordered.map((o) => o.phase),
      datasets: [
        {
          label: 'Dossiers',
          data: ordered.map((o) => o.count),
          backgroundColor: ordered.map((o) => `${PHASE_COLOR[o.phase] || '#5B7304'}CC`),
          borderColor: ordered.map((o) => PHASE_COLOR[o.phase] || '#5B7304'),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Funnel className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[280px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Funnel className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Pipeline (per fase)</CardTitle>
          <MetricInfo
            description={
              <p>
                Aantal dossiers per <code>acquisitionPhase</code>. De volgorde is
                de reële pipeline-volgorde: Inbox → Opgepikt → Agenda ACQ → Agenda
                RVB → Deal! / Afgewezen.
              </p>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Hoeveel dossiers staan in elke fase van het acquisitieproces
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          {chartData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { ...COMMON_TOOLTIP },
                },
                scales: {
                  x: { grid: { display: false } },
                  y: {
                    beginAtZero: true,
                    ticks: { precision: 0 },
                    grid: { color: 'rgba(49, 64, 59, 0.1)' },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
